import { AES, enc } from "crypto-js";
import { AccessType, PermissionType } from "@/sharedTypes/commonTypes";
import { SUPER_ADMIN_ACCESS } from "@/pages/PublicAdministration/UserAccess/types";

const accessHierarchy = {
	[AccessType.READ]: 1,
	[AccessType.GRANT]: 2,
	[AccessType.REVOKE]: 3,
	[AccessType.MANAGE]: 4
};

const permissionHierarchy = {
	[PermissionType.READ]: 1,
	[PermissionType.WRITE]: 2,
	[PermissionType.DELETE]: 3
};

export const getRolePermissions = () => {
	const encryptionKey = import.meta.env.VITE_PERMISSION_ENCRYPTION_KEY;
	const decryptedWordsArray = AES.decrypt(localStorage.encryptedRolePermissions, encryptionKey);
	const decryptedJson = enc.Utf8.stringify(decryptedWordsArray);
	const rolePermissions: Record<string, Array<string>> = JSON.parse(decryptedJson);
	return rolePermissions;
};

export const getRolePermissionsExcludingSuperAdmin = roleObj => {
	for (const key in roleObj) {
		if (key !== "super-admin") {
			return roleObj[key];
		}
	}
	return null;
};

export const hasCustomPermission = (group: string, subgroup: string, permission: string): boolean => {
	const rolePermissions = getRolePermissions();
	// Superadmin has all permissions granted, no need to checkh
	if (rolePermissions[SUPER_ADMIN_ACCESS]) {
		return true;
	}

	const userPermissions = getRolePermissionsExcludingSuperAdmin(rolePermissions);

	let hasPermission = false;
	userPermissions.forEach(userPermission => {
		if(userPermission === group || userPermission === `${group}.${subgroup}` || userPermission === `${group}.${subgroup}.${permission}`) {
			hasPermission = true;
			return;
		}
	});

	return hasPermission;
}

export const hasPermissions = (requiredPermission: string[] | string, requiredPermissionType?: PermissionType | AccessType | string): boolean => {
	const rolePermissions = getRolePermissions();
	// Superadmin has all permissions granted, no need to checkh
	if (rolePermissions[SUPER_ADMIN_ACCESS]) {
		return true;
	}

	const userPermissions = getRolePermissionsExcludingSuperAdmin(rolePermissions);
	// e.g. 'Administration' SideMenu menu group which can be accessed if any of the Administration permissions are found,
	// for that, we pass an array of all Administration permissions (Classification, IdentityManagement etc)

	if (Array.isArray(requiredPermission)) {
		const permissionMatches = userPermissions?.filter(up => requiredPermission.find(rp => rp.startsWith(up)));
		return permissionMatches.length > 0;
	}
	// From backend fetched permissions, filter out all permissions that start with 'requiredPermission' (usually 'permissionName')
	const filteredUserPermissions = userPermissions?.filter(p => p.startsWith(requiredPermission) || p === requiredPermission.split(".")[0]);

	// if no permissions with that start with 'requiredPermission' are found, user does not have any permissions
	if (!filteredUserPermissions?.length) return false;

	// flag that is set depending on the 'requiredPermissionType'
	let userHasPermission = false;
	filteredUserPermissions.forEach(permission => {
		if (userHasPermission) return true;
		// split backend fetched permissions into 3 parts, as defined on backend
		const [userMainGroup, userSubgroup, userPermission] = permission.split(".");

		// Only main group in fetched user permission groups - permissions to everything below
		if (userMainGroup && !userSubgroup) userHasPermission = true;
		// Main group and subgroup without user permission in fetched user permission groups - permissions to everything related to that subgroup
		if (userMainGroup && userSubgroup && !userPermission) userHasPermission = true;
		// Main group and subgroup without specified param 'requiredPermissionType' -  permissions to everything related to that subgroup
		if (userMainGroup && userSubgroup && !requiredPermissionType) userHasPermission = true;

		if (requiredPermissionType && !userHasPermission) {
			if (Object.values(AccessType).includes(requiredPermissionType as AccessType)) {
				const userAccessLevel = accessHierarchy[userPermission as AccessType] || 0;
				const requiredAccessLevel = accessHierarchy[requiredPermissionType as AccessType];
				if (userAccessLevel >= requiredAccessLevel) userHasPermission = true;
			} else if (Object.values(PermissionType).includes(requiredPermissionType as PermissionType)) {
				const userPermissionLevel = permissionHierarchy[userPermission as PermissionType] || 0;
				const requiredPermissionLevel = permissionHierarchy[requiredPermissionType as PermissionType];
				if (userPermissionLevel >= requiredPermissionLevel) userHasPermission = true;
			} else {
				userHasPermission = requiredPermissionType == userPermission;
			}
		}
	});

	return userHasPermission;
};
